import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from 'react-router'
import { api } from '../api/client'
import type { BildAntwort, Bildphase, Fach, Schrank } from '../api/typen'
import AppRahmen from '../komponenten/AppRahmen'
import { fachName } from '../komponenten/beschreibung'
import Kamera from '../komponenten/Kamera'

/** Ein noch nicht bestätigter Befund zu einem bereits aufgenommenen Foto. */
interface Befund {
  phase: Bildphase
  text: string
}

/**
 * Schritt 3: gefuehrte Fachaufnahme. Die Reihenfolge ist verbindlich - zuerst die Draufsicht,
 * danach die Frontalsicht (P3-01). Der Ablauf ist bewusst immer gleich, damit er sich schnell
 * abarbeiten laesst; das naechste Fach beginnt automatisch.
 *
 * <p>Der Anwender wartet nicht auf den Server: Nach dem Ausloesen erscheint sofort die Bestaetigung
 * und die zweite Aufnahme ist freigegeben. Die Uebertragungen laufen im Hintergrund, aber
 * nacheinander - zwei gleichzeitige Schreibvorgaenge auf dasselbe Fach wuerden sich sonst
 * gegenseitig ueberschreiben. Befunde der Sofortpruefung (unscharf, Duplikat) treffen daher
 * verspaetet ein und werden als Hinweis eingeblendet, statt den Ablauf anzuhalten (P4-01, P4-02).
 */
export default function FachAufnahme() {
  const { schrankId, fachNr } = useParams()
  const navigiere = useNavigate()
  const abfragen = useQueryClient()

  const [fach, setFach] = useState<Fach | null>(null)
  const [phase, setPhase] = useState<Bildphase>('oben')
  const [befunde, setBefunde] = useState<Befund[]>([])
  const [uebertraegt, setUebertraegt] = useState(0)
  const [wartetAufAuswertung, setWartetAufAuswertung] = useState(false)
  const [fehlgeschlagen, setFehlgeschlagen] = useState<{ phase: Bildphase; bild: Blob } | null>(null)

  // Die Uebertragungen haengen als Kette aneinander, damit sie sich nicht ueberholen.
  const kette = useRef<Promise<void>>(Promise.resolve())
  // Nach dem Abbrechen darf eine noch laufende Uebertragung nicht mehr zur Bestaetigung springen.
  const abgebrochen = useRef(false)

  const schrank = useQuery({
    queryKey: ['schrank', schrankId],
    queryFn: () => api.get<Schrank>(`/api/schraenke/${schrankId}`),
  })

  // Das Fach wird beim Betreten angelegt oder - falls schon vorhanden - geladen.
  const fachLaden = useMutation({
    mutationFn: () => api.post<Fach>(`/api/schraenke/${schrankId}/faecher/${fachNr}`),
    onSuccess: (geladen) => {
      setFach(geladen)
      setPhase(geladen.bildObenVorhanden && !geladen.bildVorneVorhanden ? 'vorne' : 'oben')
    },
  })

  useEffect(() => {
    abgebrochen.current = false
    setBefunde([])
    setFehlgeschlagen(null)
    fachLaden.mutate()
    // Absichtlich nur beim Wechsel des Fachs, nicht bei jeder Neuzeichnung.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schrankId, fachNr])

  /** Haengt eine Uebertragung hinten an die Kette an. */
  const uebertrage = (bild: Blob, fuerPhase: Bildphase, fachId: number) => {
    setUebertraegt((anzahl) => anzahl + 1)

    kette.current = kette.current
      .then(async () => {
        const antwort = await api.bild<BildAntwort>(
          `/api/faecher/${fachId}/bild/${fuerPhase}`,
          bild,
          `${fuerPhase}.jpg`,
        )
        if (abgebrochen.current) return
        setFach(antwort.fach)
        setFehlgeschlagen(null)

        // Der Bestaetigungsbildschirm liest dasselbe Fach ueber den Zwischenspeicher. Ohne diese
        // Zeile bekaeme er beim Betreten den Stand von vor dieser Aufnahme zu sehen.
        abfragen.setQueryData(['fach', String(antwort.fach.id)], antwort.fach)

        const neueBefunde: Befund[] = []
        if (!antwort.scharf) {
          neueBefunde.push({ phase: fuerPhase, text: beschriftung(fuerPhase) + ' wirkt unscharf.' })
        }
        if (antwort.duplikat) {
          neueBefunde.push({
            phase: fuerPhase,
            text: beschriftung(fuerPhase) + ' war bereits vorhanden und wurde ersetzt.',
          })
        }
        if (neueBefunde.length > 0) {
          setBefunde((bisher) => [...bisher.filter((b) => b.phase !== fuerPhase), ...neueBefunde])
        } else {
          setBefunde((bisher) => bisher.filter((b) => b.phase !== fuerPhase))
        }

        if (antwort.bildpaarVollstaendig) {
          navigiere(`/faecher/${antwort.fach.id}/bestaetigen`)
        }
      })
      .catch(() => {
        // Das Foto bleibt erhalten, damit der Anwender es ohne neue Aufnahme erneut senden kann.
        setFehlgeschlagen({ phase: fuerPhase, bild })
        setWartetAufAuswertung(false)
      })
      .finally(() => {
        setUebertraegt((anzahl) => Math.max(0, anzahl - 1))
      })
  }

  const aufgenommen = (bild: Blob) => {
    if (!fach) return

    uebertrage(bild, phase, fach.id)

    if (phase === 'oben') {
      // Sofort weiter zur Frontalsicht - die Draufsicht laedt im Hintergrund hoch.
      setPhase('vorne')
    } else {
      setWartetAufAuswertung(true)
    }
  }

  /**
   * Verwirft ein irrtuemlich begonnenes Fach, solange das Bildpaar unvollstaendig ist (Iteration 8).
   * Laufende Uebertragungen werden abgewartet, sonst liefe ein Foto nach dem Loeschen ins Leere
   * oder spraenge als letztes Foto des Paars noch zur Bestaetigung.
   */
  const abbrechen = useMutation({
    mutationFn: async (fachId: number) => {
      abgebrochen.current = true
      await kette.current
      await api.loeschen(`/api/faecher/${fachId}`)
      return fachId
    },
    onSuccess: (fachId) => {
      abfragen.removeQueries({ queryKey: ['fach', String(fachId)] })
      abfragen.invalidateQueries({ queryKey: ['schrank', schrankId] })
      abfragen.invalidateQueries({ queryKey: ['vollstaendigkeit', schrankId] })
      navigiere(`/schraenke/${schrankId}`)
    },
    onError: () => {
      abgebrochen.current = false
    },
  })

  // Abbrechen gibt es nur fuer ein Fach, das hier gerade erst entsteht: ohne Vorschlag, ohne
  // Ergebnis und ohne vollstaendiges Bildpaar. Ein fertiges Fach wird im Menue der Bestaetigung
  // geloescht, mit dessen eigener Rueckfrage.
  const abbrechbar =
    fach !== null &&
    !fach.kiVorschlag &&
    !fach.nutzerErgebnis &&
    !(fach.bildObenVorhanden && fach.bildVorneVorhanden)
  const fotoGemacht = phase === 'vorne' || uebertraegt > 0 || Boolean(fach?.bildObenVorhanden)

  const gesamtFaecher = schrank.data?.anzahlFaecherSoll ?? 0
  const offeneBefunde = befunde.filter((befund) => befund.phase !== phase || wartetAufAuswertung)

  return (
    <AppRahmen
      titel={schrank.data?.bezeichnung ?? 'Aufnahme'}
      unter={`Fach ${fachNr}${gesamtFaecher ? ` von ${gesamtFaecher}` : ''} · ${
        phase === 'oben' ? 'Schritt 1 von 2' : 'Schritt 2 von 2'
      }`}
      zurueck={`/schraenke/${schrankId}`}
      pfad={fach?.pfad ?? schrank.data?.pfad}
      ebene="fach"
      pfadZusatz={fach ? fachName(fach) : `Fach ${fachNr}`}
      menue={[
        {
          beschriftung: 'Zur Fachübersicht',
          beiKlick: () => navigiere(`/schraenke/${schrankId}`),
        },
        ...(abbrechbar && fach
          ? [
              {
                beschriftung: 'Aufnahme abbrechen',
                gefahr: true,
                // Ohne Foto gibt es nichts zu verlieren - dann ohne Rueckfrage.
                bestaetigung: fotoGemacht
                  ? {
                      frage: `Die bisherige Aufnahme wird verworfen und ${fachName(fach)} gelöscht. Die übrigen Fächer bleiben unverändert.`,
                      bestaetigungstext: 'Abbrechen und Fach löschen',
                    }
                  : undefined,
                beiKlick: () => abbrechen.mutate(fach.id),
              },
            ]
          : []),
      ]}
    >
      <div className="gruppe">
        <h2 className="s">{phase === 'oben' ? 'Draufsicht aufnehmen' : 'Frontalsicht aufnehmen'}</h2>
        <p className="sek">
          {phase === 'oben'
            ? 'Von oben in den Korb fotografieren. Bei sehr vollen Körben das Material kurz zusammendrücken, damit ein verdeckter Fachteiler sichtbar ist.'
            : 'Die Vorderseite des Korbs frontal fotografieren. Daraus ergibt sich die Korbhöhe.'}
        </p>
      </div>

      {offeneBefunde.map((befund) => (
        <div key={befund.phase + befund.text} className="hinweiszeile">
          <span style={{ flex: '1 1 auto' }}>{befund.text}</span>
          <button
            className="btn-dezent"
            style={{ width: 'auto', height: 'auto', padding: '0 6px' }}
            onClick={() => {
              setBefunde((bisher) => bisher.filter((b) => b !== befund))
              setWartetAufAuswertung(false)
              setPhase(befund.phase)
            }}
          >
            neu aufnehmen
          </button>
        </div>
      ))}

      {fehlgeschlagen && (
        <div className="hinweiszeile">
          <span style={{ flex: '1 1 auto' }}>
            {beschriftung(fehlgeschlagen.phase)} konnte nicht übertragen werden. Das Foto ist noch da.
          </span>
          <button
            className="btn-dezent"
            style={{ width: 'auto', height: 'auto', padding: '0 6px' }}
            onClick={() => fach && uebertrage(fehlgeschlagen.bild, fehlgeschlagen.phase, fach.id)}
          >
            erneut senden
          </button>
        </div>
      )}

      {fach ? (
        <Kamera
          hinweis={
            uebertraegt > 0
              ? `${uebertraegt} Foto${uebertraegt > 1 ? 's werden' : ' wird'} im Hintergrund übertragen …`
              : undefined
          }
          beiAufnahme={aufgenommen}
        />
      ) : (
        <div className="spinner" />
      )}

      {wartetAufAuswertung && !fehlgeschlagen && (
        <div className="ueberlagerung" role="status">
          <div className="sheet" style={{ textAlign: 'center' }}>
            <div className="spinner" />
            <h3 className="s">Auswertung läuft</h3>
            <p className="sek">
              Beide Fotos sind aufgenommen. Die Bilderkennung bestimmt gerade Korbhöhe, Fächer und Fachteiler.
            </p>
          </div>
        </div>
      )}
    </AppRahmen>
  )
}

function beschriftung(phase: Bildphase): string {
  return phase === 'oben' ? 'Die Draufsicht' : 'Die Frontalsicht'
}
